import { Component, OnInit, computed, inject, signal } from "@angular/core"
import { FormControl, FormGroup, Validators } from "@angular/forms"
import { Router } from "@angular/router"
import { Message } from "primeng/api"
import { BusinessOrderPayload } from "src/app/meta/BusinessOrderPayload.interface"
import { ConsumerOrderPayload } from "src/app/meta/ConsumerOrderPayload.interface"
import { OrderPayload } from "src/app/meta/OrderPayload.type"
import { Product } from "src/app/meta/Product.interface"
import { SharedOrderPayload } from "src/app/meta/SharedOrderPayload.interface"
import { AddressesService } from "src/app/services/addresses.service"
import { ApiService } from "src/app/services/api.service"
import { AvailabilityService } from "src/app/services/availability.service"
import { BillingDataService } from "src/app/services/billing-data.service"
import { BusinessBankingService } from "src/app/services/business-banking.service"
import { BusinessDataService } from "src/app/services/business-data.service"
import { BusinessPortingService } from "src/app/services/business-porting.service"
import { ContactPersonService } from "src/app/services/contact-person.service"
import { CustomerDataService } from "src/app/services/customer-data.service"
import { OrderService } from "src/app/services/order.service"
import { PhoneBookService } from "src/app/services/phone-book.service"
import { PortingService } from "src/app/services/porting.service"
import { ProductsService } from "src/app/services/products.service"
import { StepsService } from "src/app/services/steps.service"
import { downloadFile } from "src/app/tools/download-file"
import { environment } from "src/environments/environment"

@Component({
  selector: "ow-order-summary",
  templateUrl: "./order-summary.component.html"
})
export class OrderSummaryComponent implements OnInit {
  private readonly customerDataService = inject(CustomerDataService)
  private readonly addressesService = inject(AddressesService)
  private readonly billingDataService = inject(BillingDataService)
  private readonly portingService = inject(PortingService)
  private readonly router = inject(Router)
  private readonly apiService = inject(ApiService)
  private readonly availabilityService = inject(AvailabilityService)
  private readonly productsService = inject(ProductsService)
  private readonly stepsService = inject(StepsService)
  private readonly phoneBookService = inject(PhoneBookService)
  private readonly orderService = inject(OrderService)
  private readonly businessDataService = inject(BusinessDataService)
  private readonly contactPersonService = inject(ContactPersonService)
  private readonly businessBankingService = inject(BusinessBankingService)
  private readonly businessPortingService = inject(BusinessPortingService)

  public readonly contactData = this.customerDataService.getContactData()
  public readonly streetName = this.addressesService.getStreetName()
  public readonly streetNumber = this.addressesService.getStreetNumber()
  public readonly postalCode = this.addressesService.getPostalCode()
  public readonly city = this.addressesService.getCity()
  public readonly bankingAccount = this.billingDataService.getBankingAccount()
  public readonly billingAddress = this.billingDataService.getBillingAddres()
  public readonly portingData = this.portingService.getPortingData()
  public readonly portingNumbers = this.portingData.numbers.filter((number) => number.length > 0).join(", ")
  public readonly phoneBookData = this.phoneBookService.getPhoneBookData()
  public readonly baseProduct = this.productsService.getBaseProduct()
  public readonly demandAggregation = this.availabilityService.getAvailability()!.demandAggregation
  public readonly businessData = this.businessDataService.getBusinessData()
  public readonly contactPerson = this.contactPersonService.getContactPerson()
  public readonly businessBanking = this.businessBankingService.getBusinessBanking()
  public readonly businessPorting = this.businessPortingService.getBusinessPorting()
  public readonly agbUrl = environment.customerType === "business" ? `${environment.assetsUrl}/templates/agb_business.pdf` : `${environment.assetsUrl}/templates/agb.pdf`
  public readonly datenschutzUrl = environment.customerType === "business" ? `${environment.assetsUrl}/templates/datenschutz_business.pdf` : `${environment.assetsUrl}/templates/datenschutz.pdf`
  public readonly lbUrl = environment.customerType === "business" ? `${environment.assetsUrl}/templates/lb_business.pdf` : `${environment.assetsUrl}/templates/lb.pdf`
  public readonly pibHref = `${environment.assetsUrl}/templates/pib_${this.baseProduct!.marketingInformation.speedDown}_${this.baseProduct!.marketingInformation.contractTime}_business.pdf`
  public readonly pbHref = `${environment.assetsUrl}/templates/preisblatt_business.pdf`

  public readonly form = new FormGroup({
    contractSummaryConfirmation: new FormControl<boolean>(false, [Validators.requiredTrue]),
    legalNoticeConfirmation: new FormControl<boolean>(false, [Validators.requiredTrue]),
    optInConfirmation: new FormControl<boolean>(false)
  })
  public status = signal<"idle" | "busy" | "error" | "success">("idle")
  public messages: Message[] = []
  private readonly orderNumber = this.orderService.getOrderNumber(this.getPayload())
  public vzfStatus = signal<"pristine" | "busy" | "downloaded" | "error">("pristine")
  public vzfMessages = computed<Message[]>(() => {
    switch (this.vzfStatus()) {
      case "busy":
        return [{ severity: "info", summary: "Bitte warten", detail: "Ihre Vertragszusammenfassung wird erstellt..." }]
      case "error":
        return [{ severity: "error", summary: "Fehler", detail: "Vertragszusammenfassung konnte nicht erstellt werden, bitte erneut versuchen oder Service kontaktieren." }]
    }
    return []
  })
  public readonly customerType = environment.customerType

  ngOnInit(): void {
    this.stepsService.enableStep("order-summary")
    this.form.controls.contractSummaryConfirmation.disable()
  }

  private getSharedPayload(): SharedOrderPayload {
    const baseProcut = this.productsService.getBaseProduct() as Product
    return {
      postalCode: this.postalCode,
      city: this.city,
      streetName: this.streetName,
      streetNumber: this.streetNumber,
      addressId: this.availabilityService.getAvailability()!.addressId,
      products: this.productsService.getOrderProducts(),
      phoneBookData: this.phoneBookData,
      optInConfirmation: this.form.value.optInConfirmation!,
      bandwidth: baseProcut.marketingInformation.speedDown,
      contractTime: baseProcut.marketingInformation.contractTime ? baseProcut!.marketingInformation.contractTime : 24,
      discountMonths: baseProcut.discountMonths,
      totalMonthlyDiscountedFee: this.productsService.getTotalMonthlyDiscountedFee(),
      totalMonthlyFee: this.productsService.getTotalMonthlyFee(),
      totalStartFee: this.productsService.getTotalStartFee(),
      baseProduct: this.baseProduct!,
      cpeProduct: this.productsService.getCpeProduct()!,
      optionalProducts: this.productsService.getProductOptions(),
      hasMobileFlat: this.productsService.hasMobileFlat(),
      billingPositions: this.productsService.billingPositions()
      .filter((pos) => pos.type === "startFee" || pos.type === "monthlyOption")
      .map((pos) => ({
        name: pos.name,
        start: pos.type === "startFee" ? pos.sum : 0,
        monthly: pos.type === "monthlyOption" ? pos.sum : 0,
        type: pos.type
      })),
      monthlyDiscounts: this.productsService.monthlyDiscounts(),
      demandAggregation: this.demandAggregation,
      salescode: this.orderService.getSalescode()
    }
  }

  private getConsumerPayload(): ConsumerOrderPayload {
    return {
      ...this.getSharedPayload(),
      customerType: "consumer",
      contactData: this.contactData,
      billingAddress: this.billingAddress,
      bankingAccount: this.bankingAccount,
      portingData: this.portingData
    }
  }

  private getBusinessPayload(): BusinessOrderPayload {
    return {
      ...this.getSharedPayload(),
      customerType: "business",
      businessData: this.businessData,
      contactPerson: this.contactPerson,
      businessBanking: this.businessBanking,
      businessPorting: this.businessPorting
    }
  }

  private getPayload(): OrderPayload {
    return environment.customerType === "business" ? this.getBusinessPayload() : this.getConsumerPayload()
  }

  public generateVzf(): void {
    if (this.vzfStatus() === "busy") {
      return
    }
    this.vzfStatus.set("busy")
    this.apiService.generateVzf(this.getPayload(), this.orderNumber).subscribe({
      next: (vzf) => {
        downloadFile(vzf, "application/pdf", `vzf_${this.orderNumber}.pdf`)
        this.form.controls.contractSummaryConfirmation.enable()
        this.vzfStatus.set("downloaded")
      },
      error: () => {
        this.form.controls.contractSummaryConfirmation.disable()
        this.vzfStatus.set("error")
      }
    })
  }

  public submit(): void {
    this.status.set("busy")
    this.messages = []
    this.apiService.postOrder(this.getPayload(), this.orderNumber).subscribe({
      next: () => {
        this.status.set("success")
        this.messages = [{ severity: "success", summary: "Bestellung erfolgreich", detail: "Ihre Bestellung wurde erfolgreich übermittelt. Sie erhalten in Kürze eine Auftragseingangsbestätigung per E-Mail." }]
      },
      error: (error) => {
        this.status.set("error")
        if (error.error && error.error.message) {
          this.messages = [{ severity: "error", summary: "Fehler", detail: error.error.message }]
        } else {
          this.messages = [{ severity: "error", summary: "Fehler", detail: "Bestellung fehlgeschlagen. Bitte erneut versuchen oder Service anrufen." }]
        }
      }
    })
  }

  public prevStep(): void {
    this.router.navigateByUrl("/data-form")
  } 

}
